import { useTranslation } from 'react-i18next';
import { AlertCircle, AlertTriangle, Info } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn, formatDate } from '@/lib/utils';
import { useRenewalSummary } from '../hooks/useSubscriptions';
import type { RenewalWarning } from '../types';

interface RenewalSummaryPanelProps {
  subscriptionId: string | null;
}

const SEVERITY_STYLE: Record<string, string> = {
  high: 'border-red-200 bg-red-50 text-red-700',
  medium: 'border-amber-200 bg-amber-50 text-amber-700',
  info: 'border-blue-200 bg-blue-50 text-blue-700',
};

/** English fallbacks — the DAO emits stable codes; both locales live in subscriptions.json. */
const WARNING_FALLBACK: Record<string, string> = {
  no_reference_invoice: 'No recurring reference invoice found for this subscription.',
  membership_not_curated: 'Sensor membership is not curated yet — link this subscription’s sensors before generating.',
  channel_count_differs: 'The live sensor count differs from the last invoiced quantity.',
  unlinked_sensors_on_account: 'The account has active sensors not linked to any subscription.',
  lump_sum_line: 'The support line is a lump sum — it will be copied untouched, not recounted.',
  tier_bracket_crossed: 'The sensor count has crossed a pricing bracket — the price is kept; verify.',
  calibration_arrangement_other: 'Special calibration arrangement — verify the calibration line.',
  calibration_lines_flags_disagree: 'Calibration lines and sensor calibration flags disagree — check the calibration line or the sensors’ Calibration flags.',
  sim_usage_unknown: 'SIM count is administrative only; real 12-month usage is not available yet.',
  sim_count_account_scoped: 'SIM count is account-wide; this account has several subscriptions.',
  multiple_active_subscriptions: 'This account has several active subscriptions.',
};

function WarningIcon({ severity }: { severity: string }) {
  const className = 'mt-0.5 h-3.5 w-3.5 shrink-0';
  if (severity === 'high') return <AlertCircle className={className} />;
  if (severity === 'medium') return <AlertTriangle className={className} />;
  return <Info className={className} />;
}

/**
 * Read-only renewal summary for a single subscription (S2b-1). Surfaces the
 * three channel counts, the reference invoice, and the DAO's warnings from
 * GET /v4/subscriptions/:id/renewal-summary. No writes, no generate button.
 */
export default function RenewalSummaryPanel({ subscriptionId }: RenewalSummaryPanelProps) {
  const { t } = useTranslation('subscriptions');
  const { data, isLoading, isError } = useRenewalSummary(subscriptionId);

  if (!subscriptionId) {
    return (
      <p className="text-sm text-muted-foreground">
        {t('renewal.saveFirst', 'Save the subscription first to see its renewal summary.')}
      </p>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-3" data-testid="renewal-summary-loading">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <p className="text-sm text-muted-foreground" role="alert">
        {t('renewal.loadError', 'Could not load the renewal summary.')}
      </p>
    );
  }

  const { channels, referenceInvoice, warnings } = data;

  const stats = [
    { key: 'linked', label: t('renewal.linkedChannels', 'Linked sensors'), value: channels.linked },
    { key: 'account', label: t('renewal.accountChannels', 'Account sensors'), value: channels.account },
    { key: 'unlinked', label: t('renewal.unlinkedChannels', 'Unlinked sensors'), value: channels.unlinked },
  ];

  return (
    <div className="space-y-4" data-testid="renewal-summary">
      <div className="grid grid-cols-3 gap-3">
        {stats.map((s) => (
          <div
            key={s.key}
            className="rounded-lg border bg-card p-3 text-center"
            data-testid={`renewal-stat-${s.key}`}
          >
            <div className="text-2xl font-semibold tabular-nums">{s.value}</div>
            <div className="text-xs text-muted-foreground">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="text-sm">
        <span className="text-muted-foreground">{t('renewal.referenceInvoice', 'Reference invoice')}: </span>
        {referenceInvoice ? (
          <span className="font-medium" data-testid="renewal-reference-invoice">
            #{referenceInvoice.invoiceNumber ?? '—'}
            {referenceInvoice.invoiceDate ? ` · ${formatDate(referenceInvoice.invoiceDate)}` : ''}
          </span>
        ) : (
          <span className="italic text-muted-foreground">{t('renewal.noReferenceInvoice', 'None on record')}</span>
        )}
      </div>

      {warnings.length > 0 && (
        <ul className="space-y-2" data-testid="renewal-warnings">
          {warnings.map((w: RenewalWarning, i: number) => (
            <li
              key={`${w.code}-${i}`}
              className={cn(
                'flex items-start gap-2 rounded-md border px-3 py-2 text-xs',
                SEVERITY_STYLE[w.severity] ?? SEVERITY_STYLE.info,
              )}
              data-testid={`renewal-warning-${w.code}`}
            >
              <WarningIcon severity={w.severity} />
              <span>{t(`renewal.warnings.${w.code}`, WARNING_FALLBACK[w.code] ?? w.code)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
